import React, {useEffect, useState} from 'react';
import {format} from 'date-fns';
import {Droppable, Draggable} from 'react-beautiful-dnd';
import styled, {css} from 'styled-components';

import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';

import {TargetArrow} from '@styled-icons/fluentui-system-filled/TargetArrow';
import {WeatherSunny} from '@styled-icons/fluentui-system-filled/WeatherSunny';
import {Info} from '@styled-icons/fluentui-system-filled/Info';
import {Label2, Label3, Body4} from '@bootcamp/web/src/components/Typography/next';
import {useTooltipState} from '../contexts/Tooltip';

import {InputComponent, CustomInputWrapper} from '@bootcamp/web/src/components/UI/inputs';

import AssignmentModal, {ExamDateModal, ExpirationDateModal, CustomAssignmentModal} from './Modals/Assignment';
import {formatEstimatedTime} from '../helpers';
import theme from '@bootcamp/shared/src/styles/theme';
import nanoid from 'nanoid';
import {Add} from '@styled-icons/fluentui-system-filled/Add';

const AddAssignment = styled.div`
  cursor: pointer;
  background: #FAFAFA;
  border: 1px solid ${({theme}) => theme.colors.brandPalette.grey.default};
  border-radius: 4px;
  padding: 4px 0px 8px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity .2s;
  ${({theme}) => theme.mediaQueries.tablet} {
    opacity: 1;
    height: 36px;
  }
`

const CompletedAssignmentVisibilityToggle = styled.div`
  opacity: 0;
  cursor: pointer;
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 14.4px */
  margin-top: 30px;
  margin-bottom: 4px;
  user-select: none;
  transition: opacity .2s;
  ${({hoverDisabled}) => !hoverDisabled && css`
    &:hover {
      color: ${({theme}) => theme.colors.brandPalette.royal.default};
    }
  `}
  ${({theme}) => theme.mediaQueries.tablet} {
    opacity: 1;
  }
`

const CalendarDayContainer = styled.div`
  background: ${({theme, isExamDate, inactive}) => inactive ? 'rgb(250,250,250)' : isExamDate ? theme.colors.interfacePalette.red.light : theme.colors.neutralsPalette.white};
  display: flex;
  flex-direction: column;
  padding: 4px;
  border-left: ${({theme}) => `1px solid ${theme.colors.neutralsPalette.light}`};
  &:first-child {
    border-left: none;
  }
  ${({dividerLineState, theme}) => ['top', 'left'].includes(dividerLineState) ? css`
    border-top: 2px solid ${theme.colors.neutralsPalette.light};
    ${dividerLineState === 'left' && css`border-left: 3px solid ${theme.colors.neutralsPalette.light};`}
  ` : ['bottom'].includes(dividerLineState) ? css`border-bottom: 2px solid ${theme.colors.neutralsPalette.light};` : ''}
  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 12px;
  }
  min-width: 0px;
  position: relative;
  &:hover {
    ${CompletedAssignmentVisibilityToggle} {
      opacity: 1;
    }
    ${AddAssignment} {
      opacity: 1;
    }

  }
`;

const CalendarDayHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`
const CalendarDayHeaderNumber = styled(Label2)`
  color: ${({theme, inactive, current}) => inactive && !current ? theme.colors.typographyPalette.tertiary : current ? theme.colors.neutralsPalette.white : theme.colors.typographyPalette.primary};
  padding: 4px;
  ${({current}) => current && css`
    background: ${({theme}) => theme.colors.brandPalette.rose.default};
    border-radius: 4px;
  `}
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({theme}) => theme.mediaQueries.tablet} {
    width: auto;
    height: auto;
    justify-content: flex-start;
    padding: 0px;
    font-weight: 700;
  }
`;

const CalendarDayHeaderMonth = styled(Label3)`
  margin-left: 0;
  margin-right: auto;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  font-style: italic;
  font-size: 16px;
  font-weight: 400;
`;

const CalendarDayHeaderEstTime = styled(Label3)`
  margin-left: auto;
  margin-right: 0;
  font-weight: 400;
  color: ${({theme}) => theme.colors.typographyPalette.tertiary};
`;

const CalendarDayEventList = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 2px;
  gap: ${({theme}) => theme.layouts.spacing.xs};
  flex: 1;
  min-height: 100px;
  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 8px 0px;
  }
`;

const CalendarDayOutline = styled.div`
  outline: ${({theme, isDraggingOver}) => isDraggingOver ? `2px dotted ${theme.colors.brandPalette.royal.default}` : `none`};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CalendarDayEvent = styled.div`
  position: relative;
  background: ${({theme, color}) => theme.colors.assignmentColorPalette[color || 'grey'].light};
  color: ${({theme, color}) => theme.colors.assignmentColorPalette[color || 'grey'].dark};
  border: ${({theme, color}) => `1px solid ${theme.colors.assignmentColorPalette[color || 'grey'].dark}1a`};
  border-radius: 4px;
  padding: 4px 4px 8px;
  display: flex;
  gap: 4px;
  flex-direction: column;
  align-items: flex-start;
  cursor: ${({isDragging}) => isDragging ? 'pointer' : 'pointer'} !important;
  input[type="checkbox"] {
    width: 14px;
    height: 14px;
    accent-color: ${({theme, color}) => theme.colors.assignmentColorPalette[color || 'grey'].dark};
    background: transparent;
    cursor: pointer;
  }

  width: 100%;
  font-size: 12px;
  font-weight: 400;
  ${({theme}) => theme.mediaQueries.tablet} {
    font-size: 14px;
  }
  ${({disabled}) => !disabled && css`
    &:hover {
      border: ${({theme, color}) => `1px solid ${theme.colors.assignmentColorPalette[color || 'grey'].default}14`};
      background: ${({theme, color}) => theme.colors.assignmentColorPalette[color || 'grey'].hover};
    }
  `}
  ${({complete}) => complete && css`
    background: ${({theme, color}) => theme.colors.assignmentColorPalette[color || 'grey'].light}40;
    color: ${({theme, color}) => theme.colors.assignmentColorPalette[color || 'grey'].dark}40;
    input[type="checkbox"] {
      accent-color: ${({theme, color}) => theme.colors.assignmentColorPalette[color || 'grey'].dark}40;
    }
    text-decoration: line-through;
    &:hover {
      border: ${({theme, color}) => `1px solid ${theme.colors.assignmentColorPalette[color || 'grey'].default}1a`};
      background: ${({theme, color}) => theme.colors.assignmentColorPalette[color || 'grey'].light}40;
    }
  `}
`;
const CalendarDayEventWrapper = styled.div`
  position: relative;
`;
const CalendarDayEventText= styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-wrap: pretty;
  overflow: hidden;
  width: 100%;
  gap: 6px;
  ${({shortened}) => shortened && css`
  align-items: center;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  `}

  ${({hasTitle}) => !hasTitle && css`
    span {
      color: ${({theme}) => theme.colors.typographyPalette.secondary};
    }
  `}
`;

export const typeDetails = {
  custom: {
    color: 'grey',
    label: 'Custom',
  },
  qbank: {
    color: 'blue',
    label: 'Qbank',
  },
  bites: {
    color: 'orange',
    label: 'Bites',
  },
  createTest: {
    color: 'violet',
    label: 'Create Test',
  },
  resource: {
    color: 'indigo',
    label: 'Resource',
  },
  video: {
    color: 'teal',
    label: 'Video',
  },
  practiceTest: {
    color: 'red',
    label: 'Practice Test',
  },
}

export const subjectDetails = {
  dat: {
    Biology: {
      color: 'blue',
      label: 'Biology',
    },
    "General Chemistry": {
      color: 'teal',
      label: 'General Chemistry',
    },
    "Organic Chemistry": {
      color: 'orange',
      label: 'Organic Chemistry',
    },
    "Perceptual Ability": {
      color: 'violet',
      label: 'Perceptual Ability',
    },
    "Quantitative Reasoning": {
      color: 'indigo',
      label: 'Quantitative Reasoning',
    },
    "Reading Comprehension": {
      color: 'fuschia',
      label: 'Reading Comprehension',
    },
  },
  oat: {
    Biology: {
      color: 'blue',
      label: 'Biology',
    },
    "General Chemistry": {
      color: 'teal',
      label: 'General Chemistry',
    },
    "Organic Chemistry": {
      color: 'orange',
      label: 'Organic Chemistry',
    },
    "Physics": {
      color: 'violet',
      label: 'Physics',
    },
    "Quantitative Reasoning": {
      color: 'indigo',
      label: 'Quantitative Reasoning',
    },
    "Reading Comprehension": {
      color: 'fuschia',
      label: 'Reading Comprehension',
    },
  },
  nclex: {},
  "med-school": {
    Gastroenterology: {
      color: 'honey',
      label: 'Gastroenterology',
    },
    Nephrology: {
      color: 'sienna',
      label: 'Nephrology',
    },
    Genetics: {
      color: 'crimson',
      label: 'Genetics',
    },
    Biostatistics: {
      color: 'magenta',
      label: 'Biostatistics',
    },
    Neurology: {
      color: 'lilac',
      label: 'Neurology',
    },
    "Public Health Sciences": {
      color: 'periwinkle',
      label: 'Public Health Sciences',
    },
    Cardiology: {
      color: 'plum',
      label: 'Cardiology',
    },
    Pharmacology: {
      color: 'mocha',
      label: 'Pharmacology',
    },
    'Pharmacology (Principles)': {
      color: 'mocha',
      label: 'Pharmacology (Principles)',
    },
    Psychiatry: {
      color: 'thyme',
      label: 'Psychiatry',
    },
    Endocrinology: {
      color: 'olive',
      label: 'Endocrinology',
    },
    Pulmonology: {
      color: 'moss',
      label: 'Pulmonology',
    },
    'Hematology & Oncology': {
      color: 'fern',
      label: 'Hematology & Oncology',
    },
    Immunology: {
      color: 'sage',
      label: 'Immunology',
    },
    Reproduction: {
      color: 'mint',
      label: 'Reproduction',
    },
    Biochemistry: {
      color: 'aqua',
      label: 'Biochemistry',
    },
    Microbiology: {
      color: 'denim',
      label: 'Microbiology',
    },
    Dermatology: {
      color: 'lavender',
      label: 'Dermatology',
    },
    Musculoskeletal: {
      color: 'steel',
      label: 'Musculoskeletal',
    },
    "Pathology (Principles)": {
      color: 'blue',
      label: 'Pathology (Principles)',
    },
    Osteopathy: {
      color: 'teal',
      label: 'Osteopathy',
    }
  },
  inbde: {
    "Fields of Dentistry": {
      color: 'blue',
      label: 'Fields of Dentistry',
    },
    "Oral Pathology and Oral Radiology": {
      color: 'teal',
      label: 'Oral Pathology and Oral Radiology',
    },
    "Medicine": {
      color: 'orange',
      label: 'Medicine',
    },
    "Pharmacology": {
      color: 'violet',
      label: 'Pharmacology',
    },
    "Clinical Management": {
      color: 'indigo',
      label: 'Clinical Management',
    },
    "Anatomy and Physiology": {
      color: 'fuschia',
      label: 'Anatomy and Physiology',
    },
    "Research": {
      color: 'cyan',
      label: 'Research',
    },
    "Case Sets": {
      color: 'green',
      label: 'Case Sets',
    },
    "Simulation Exam": {
      color: 'red',
      label: 'Simulation Exam',
    }
  },
}

const ExamDayContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 4px 0px 24px;
  position: relative;
`

const ExamDayText = styled(Label2)`
  color: ${({theme}) => theme.colors.interfacePalette.red.dark};
`;

const ExamDayIcon = styled(TargetArrow)`
  color: ${({theme}) => theme.colors.interfacePalette.red.default};
  width: 32px;
  height: 32px;
  margin-bottom: 8px;
  cursor: pointer;
`;

const RestDayText = styled(Label2)`
  color: ${({theme}) => theme.colors.typographyPalette.tertiary};
`;
const RestDayIcon = styled(WeatherSunny)`
  color: ${({theme}) => theme.colors.typographyPalette.tertiary};
  width: 32px;
  height: 32px;
  margin-bottom: 8px;
`;

const ExpirationDayContainer = styled(ExamDayContainer)`
  flex: 0;
  margin-top: 4px;
  cursor: pointer;
`;

const ExpirationDayText = styled(Label3)`
  color: ${({theme}) => theme.colors.interfacePalette.red.default};
  font-size: 12px;
  font-weight: 400;
`;
const ExpirationDayIcon = styled(Info)`
  color: ${({theme}) => theme.colors.interfacePalette.red.default};
  width: 14px;
  height: 14px;
  margin-bottom: 4px;
`;
const EstimatedTimeText = styled(Body4)`
  opacity: 0.5;
  margin-left: auto;
  margin-top: 2px;
  line-height: 100%;
  font-size: 12px;
`;
const StyledCheckbox = styled(InputComponent)`
  ${({checked}) => checked && css`
    opacity: .25;
    transition: opacity .2s;
    &:hover {
      opacity: 1;
    }
  `}
  ${({disabled}) => disabled && css`
    &:hover {
      ${CustomInputWrapper} {
        background: white; 
      }
    }
  `}
`;


export const AssignmentWithoutContext = ({event, provided, updateCompleteStatus, showEstimatedTime}) => {
  const {searchUserInteractions, bootcamp} = useUserDataContext();
  const useSubjectColors = searchUserInteractions('useSubjectColors', false) && ['dat', 'inbde', 'oat', 'med-school'].includes(bootcamp);
  const palette = useSubjectColors ? subjectDetails[bootcamp][event?.subject]?.color : typeDetails[event.type].color;
  const studySpeed = event?.custom ? 1 : searchUserInteractions('studySpeed', 1);
  const estimatedTime = parseInt(event.estimatedTime) * studySpeed;
  return (
    <CalendarDayEvent disabled={event.disabled} color={palette} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} complete={event.complete}
    data-tooltip-id={event.id}
    data-tooltip-events={'click'}
  >
    <CalendarDayEventText shortened={!showEstimatedTime} title={event.title}>
        <StyledCheckbox
          key={event.complete}
          active={false}
          checked={event.complete}
          clickDisabled
          size={14}
        />
      <span>{event.title}</span>
    </CalendarDayEventText>
    {estimatedTime > 0 && showEstimatedTime &&
      <EstimatedTimeText>
        {formatEstimatedTime(estimatedTime)}
      </EstimatedTimeText>
    }
  </CalendarDayEvent>
  );
}

export const Assignment = ({event, date, startDate, provided, updateCompleteStatus, updateAssignmentDate, updateEvent, showEstimatedTime, isDragging, createAssignmentOnDate}) => {
  const {tooltipId, setTooltipId} = useTooltipState();
  const [checked, setChecked] = useState(event.complete);
  
  useEffect(() => {
    if (event.complete !== checked) {
      setChecked(event.complete);
    }
  }, [event]);

  const handleInputClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const updatedValue = !checked;

    setChecked(updatedValue);
    updateCompleteStatus(event.id, updatedValue);
  };

  const handleInputChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleAssignmentClick = (e) => {
    if (e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      window.open(event.externalLink, '_blank');
      return;
    }
    setTooltipId(event.id)
  };
  const {searchUserInteractions, bootcamp} = useUserDataContext();
  const useSubjectColors = searchUserInteractions('useSubjectColors', false) && ['dat', 'inbde', 'oat', 'med-school'].includes(bootcamp);
  const palette = event?.custom ? typeDetails[event.type].color : useSubjectColors && !event?.subject && event?.title.match('Full Length') ? 'red' : useSubjectColors ? subjectDetails[bootcamp][event?.subject]?.color : typeDetails[event.type].color;
  const studySpeed = event?.custom ? 1 : searchUserInteractions('studySpeed', 1);
  const estimatedTime = parseInt(event.estimatedTime || 0) * studySpeed;

  return (
    <CalendarDayEventWrapper>
      <CalendarDayEvent color={palette} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} complete={checked}
        data-tooltip-id={event.id}
        data-tooltip-events={'click'}
        onClick={handleAssignmentClick}
        isDragging={isDragging}
      >
        <CalendarDayEventText hasTitle={!!event?.title} shortened={!showEstimatedTime} title={!showEstimatedTime && event.title ? event.title : undefined}>
          <StyledCheckbox
            key={checked}
            checked={checked}
            onClick={handleInputClick}
            customTheme={theme.colors.assignmentColorPalette[palette] || theme.colors.assignmentColorPalette.grey}
            active={true}
            size={14}
          />
          <span>{event.title || 'New assignment'}</span>
        </CalendarDayEventText>
        {estimatedTime > 0 && showEstimatedTime &&
          <EstimatedTimeText>
            {formatEstimatedTime(estimatedTime)}
          </EstimatedTimeText>
        }
      </CalendarDayEvent>
      {event.id === tooltipId && !event?.custom &&
        <AssignmentModal
          date={date}
          startDate={startDate}
          event={event}
          checked={checked}
          onInputClick={handleInputClick}
          updateAssignmentDate={updateAssignmentDate}
        />
      }
      {event.id === tooltipId && event?.custom && (
        <CustomAssignmentModal
          date={date}
          startDate={startDate}
          event={event}
          checked={checked}
          onInputClick={handleInputClick}
          updateEvent={updateEvent}
          updateAssignmentDate={updateAssignmentDate}
          createAssignmentOnDate={createAssignmentOnDate}
        />
      )}
    </CalendarDayEventWrapper>
  );
}

export const CalendarDayWithoutContext = ({events, hideCompletedAssignments, ...props}) => {
  return (
    <CalendarDayContainer style={{border: '1px solid #EDEDF0'}}>
      <CalendarDayHeader>
        <CalendarDayHeaderNumber>1</CalendarDayHeaderNumber>
      </CalendarDayHeader>
      {events.filter(event => hideCompletedAssignments ? !event.complete : true).length > 0 && <CalendarDayEventList style={{minHeight: 0}}>
        {events.filter(event => hideCompletedAssignments ? !event.complete : true).map((event, index) => (
          <AssignmentWithoutContext {...props} event={event} />
        ))}
      </CalendarDayEventList>}
      {hideCompletedAssignments && events?.filter(event => event.complete).length > 0 && (
        <CompletedAssignmentVisibilityToggle hoverDisabled style={{opacity: 1, marginTop: 46}}>
          {'Show'} {events?.filter(event => event.complete)?.length} completed
        </CompletedAssignmentVisibilityToggle>
      )}

    </CalendarDayContainer>
  )
}

const CalendarDay = ({date, startDate, examDate, isPresent, events, isExamDate, isExpirationDate, updateCompleteStatus, updateAssignmentDate, updateEvent, createAssignmentOnDate, documentWidth, membershipExpirationDate, dividerLineState}) => {
  const {tooltipId, setTooltipId} = useTooltipState();
  const {searchUserInteractions, bootcamp} = useUserDataContext();
  const formattedDate = format(date, 'yyyy-MM-dd');
  const dayOfMonth = date.getDate();
  const isToday = date.toDateString() === new Date().toDateString();
  const showTimeInAssignment = !!searchUserInteractions('showTimeInAssignment', 1);
  const hideCompletedAssignments = searchUserInteractions('hideCompletedAssignments', false);
  const [showingHiddenAssignments, setShowingHiddenAssignments] = useState(false);
  const studySpeed = searchUserInteractions('studySpeed', 1);
  const estTime = events
    ?.filter(event => !event.complete)
    ?.reduce((acc, event) => acc + (parseInt(event.estimatedTime || 0) * (event?.custom ? 1 : studySpeed)), 0) || 0;

  const formattedEstTime = formatEstimatedTime(estTime);

  if ((formattedDate > examDate || !isPresent) && (events === undefined || events.length === 0)) {
    return (
      <CalendarDayContainer inactive key={date}>
        <CalendarDayHeader>
          <CalendarDayHeaderNumber inactive>{dayOfMonth}</CalendarDayHeaderNumber>
        </CalendarDayHeader>
        <CalendarDayEventList />
      </CalendarDayContainer>
    );
  }

  const header = documentWidth <= 768 ? (
    <CalendarDayHeader>
      <CalendarDayHeaderNumber>{isToday ? 'Today' : format(date, 'E')}, {format(date, 'MMM d')}</CalendarDayHeaderNumber>
      {/* {dayOfMonth === 1 && <CalendarDayHeaderMonth>{format(date, 'MMM')}</CalendarDayHeaderMonth>} */}
      {formattedEstTime != '0h' && <CalendarDayHeaderEstTime estimatedTime={estTime} children={formattedEstTime} />}
    </CalendarDayHeader>
  ) : (
      <CalendarDayHeader>
        <CalendarDayHeaderNumber inactive={!isPresent} current={isToday}>{dayOfMonth}</CalendarDayHeaderNumber>
        {dayOfMonth === 1 && <CalendarDayHeaderMonth>{format(date, 'MMM')}</CalendarDayHeaderMonth>}
        {formattedEstTime !== '0h' && <CalendarDayHeaderEstTime estimatedTime={estTime} children={formattedEstTime} />}
      </CalendarDayHeader>
  )
  const ExpirationDay = () => (
    <ExpirationDayContainer onClick={() => {
      setTooltipId('expirationDate');
    }}>
      <ExpirationDayIcon />
      <ExpirationDayText>Membership Ends</ExpirationDayText>
    </ExpirationDayContainer>
  );

  const AddAssignmentSection = !isExamDate && (
    <AddAssignment onClick={() => {
      const newEvent = {
        title: '',
        estimatedTime: 15,
        type: 'custom',
        id: nanoid(),
        bootcamp,
        subject: null,
        externalLink: null,
        description: null,
        complete: false,
        custom: true
      }
      createAssignmentOnDate(formattedDate, newEvent);
      setTooltipId(newEvent.id);
    }}>
      <Add size={14} />
    </AddAssignment>
  )


  if (!events?.length && isPresent && !isExamDate) {
    return (
      <Droppable droppableId={formattedDate}>
        {(provided, snapshot) => (
          <CalendarDayContainer key={date} dividerLineState={dividerLineState}>
            {
              tooltipId === 'expirationDate' && isExpirationDate &&
              <ExpirationDateModal membershipExpirationDate={membershipExpirationDate} />
            }
            <CalendarDayOutline isDraggingOver={snapshot.isDraggingOver}>
              <CalendarDayHeader>
                {header}
              </CalendarDayHeader>
              {isExpirationDate && <ExpirationDay />}
              <CalendarDayEventList ref={provided.innerRef} {...provided.droppableProps}>
                {provided.placeholder}
                {!snapshot.isDraggingOver && <ExamDayContainer style={{justifyContent: 'flex-start'}}>
                  <RestDayIcon />
                  <RestDayText>Day Off</RestDayText>
                </ExamDayContainer>}
                {AddAssignmentSection}
              </CalendarDayEventList>
            </CalendarDayOutline>
          </CalendarDayContainer>
        )}
      </Droppable>
    );
  }
  return (
    <Droppable droppableId={formattedDate}>
      {(provided, snapshot) => (
        <CalendarDayContainer key={date} isExamDate={isExamDate} className={format(date, 'yyyy-MM')} dividerLineState={dividerLineState}>
          {tooltipId === 'examDate' && isExamDate &&
            <ExamDateModal />
          }
          {
            tooltipId === 'expirationDate' && isExpirationDate &&
            <ExpirationDateModal membershipExpirationDate={membershipExpirationDate} />
          }
          <CalendarDayOutline isDraggingOver={snapshot.isDraggingOver}>
            {header}
            {isExpirationDate && <ExpirationDay />}
            {isExamDate && (
              <ExamDayContainer onClick={() => setTooltipId('examDate')}>
                <ExamDayIcon />
                <ExamDayText>
                  Exam Day
                </ExamDayText>
              </ExamDayContainer>
            )}
            <CalendarDayEventList ref={provided.innerRef} {...provided.droppableProps} >
              {events?.filter(event => hideCompletedAssignments && !showingHiddenAssignments ? !event.complete : true).map((event, index) => (
                <Draggable isDragDisabled={documentWidth <= 768} key={event.id} draggableId={event.id.toString()} index={index}>
                  {(provided, dragProps) => {
                    return (
                      <Assignment
                        event={event}
                        provided={provided}
                        updateCompleteStatus={updateCompleteStatus}
                        updateAssignmentDate={updateAssignmentDate}
                        updateEvent={updateEvent}
                        showEstimatedTime={showTimeInAssignment}
                        date={date}
                        isDragging={dragProps.isDragging}
                        startDate={startDate} 
                        createAssignmentOnDate={createAssignmentOnDate}
                      />
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
              {AddAssignmentSection}
            </CalendarDayEventList>
            {hideCompletedAssignments && events?.filter(event => event.complete).length > 0 && (
              <CompletedAssignmentVisibilityToggle onClick={() => setShowingHiddenAssignments(!showingHiddenAssignments)}>
                {showingHiddenAssignments ? 'Hide' : 'Show'} {events?.filter(event => event.complete)?.length} completed
              </CompletedAssignmentVisibilityToggle>
            )}
          </CalendarDayOutline>
        </CalendarDayContainer>
      )}
    </Droppable>
  );
}


export default CalendarDay;